export interface JobTitleInfo {
  title: string
  description: string
  medianSalary: string
  growthRate: string
  education: string
}

export const jobTitleDetails: Record<number, JobTitleInfo[]> = {
  1: [
    {
      title: 'Financial Analyst',
      description:
        'Financial analysts evaluate investment opportunities, analyze financial data, and guide businesses and individuals in making sound financial decisions. They study economic trends, review company financials, and create models to forecast future performance.',
      medianSalary: '$95,080',
      growthRate: '8% (faster than average)',
      education: "Bachelor's in Business, Finance, or Accounting",
    },
    {
      title: 'Marketing Manager',
      description:
        'Marketing managers plan and direct marketing campaigns, identify demand for products and services, and develop pricing strategies. They oversee teams of specialists and coordinate with sales, product development, and advertising departments.',
      medianSalary: '$140,040',
      growthRate: '6% (faster than average)',
      education: "Bachelor's in Marketing, Business, or Communications",
    },
    {
      title: 'Human Resources Specialist',
      description:
        'HR specialists recruit, screen, and interview job applicants, handle employee relations, and administer compensation and benefits programs. They play a critical role in building organizational culture and ensuring compliance with labor laws.',
      medianSalary: '$67,650',
      growthRate: '6% (faster than average)',
      education:
        "Bachelor's in Human Resources, Business Administration, or Psychology",
    },
    {
      title: 'Accountant',
      description:
        'Accountants prepare and examine financial records, ensure taxes are paid properly and on time, and assess financial operations to help organizations run efficiently. Many specialize in areas like tax, auditing, or forensic accounting.',
      medianSalary: '$79,880',
      growthRate: '4% (as fast as average)',
      education: "Bachelor's in Accounting or Finance (CPA preferred)",
    },
    {
      title: 'Operations Manager',
      description:
        'Operations managers oversee the production of goods and services, coordinate activities across departments, and develop strategies to maximize efficiency and quality. They are essential in manufacturing, logistics, healthcare, and technology sectors.',
      medianSalary: '$101,280',
      growthRate: '4% (as fast as average)',
      education:
        "Bachelor's in Business Administration or Operations Management",
    },
  ],
  2: [
    {
      title: 'Police Officer',
      description:
        'Police officers enforce laws, respond to emergencies, patrol assigned areas, and investigate crimes. They work to maintain public safety and order in communities, often specializing in areas like narcotics, traffic, or community policing.',
      medianSalary: '$65,790',
      growthRate: '3% (about average)',
      education:
        "Associate's or Bachelor's in Criminal Justice (varies by department)",
    },
    {
      title: 'Probation Officer',
      description:
        'Probation officers supervise offenders who have been sentenced to probation instead of prison. They monitor compliance with court orders, conduct home visits, and connect offenders with rehabilitation services and community resources.',
      medianSalary: '$60,250',
      growthRate: '3% (about average)',
      education: "Bachelor's in Criminal Justice, Social Work, or Psychology",
    },
    {
      title: 'Paralegal',
      description:
        'Paralegals assist lawyers by conducting legal research, drafting documents, organizing case files, and preparing for trials. They work in law firms, corporate legal departments, and government agencies across all areas of law.',
      medianSalary: '$59,200',
      growthRate: '4% (as fast as average)',
      education:
        "Associate's or Bachelor's in Paralegal Studies or Criminal Justice",
    },
    {
      title: 'Private Investigator',
      description:
        'Private investigators gather facts and analyze information about legal, financial, and personal matters. They conduct surveillance, interview witnesses, search public records, and provide findings to attorneys, businesses, and individuals.',
      medianSalary: '$59,380',
      growthRate: '6% (faster than average)',
      education:
        "Bachelor's in Criminal Justice or related field (license required in most states)",
    },
    {
      title: 'Intelligence Analyst',
      description:
        'Intelligence analysts collect and evaluate information from various sources to identify threats and support decision-making in law enforcement, military, and national security agencies. They use data analysis, geospatial tools, and critical thinking.',
      medianSalary: '$74,870',
      growthRate: '7% (faster than average)',
      education:
        "Bachelor's in Criminal Justice, Intelligence Studies, or Political Science",
    },
  ],
  3: [
    {
      title: 'Substance Abuse Counselor',
      description:
        'Substance abuse counselors help individuals struggling with addiction to drugs, alcohol, and other substances. They develop treatment plans, lead therapy sessions, and support clients through recovery, often working in rehabilitation centers and community health organizations.',
      medianSalary: '$53,710',
      growthRate: '18% (much faster than average)',
      education:
        "Bachelor's in Psychology or Counseling (Master's for licensure)",
    },
    {
      title: 'Human Resources Specialist',
      description:
        'With a psychology background, HR specialists bring unique insight into employee behavior, motivation, and organizational dynamics. They handle recruitment, training, conflict resolution, and employee wellness programs.',
      medianSalary: '$67,650',
      growthRate: '6% (faster than average)',
      education:
        "Bachelor's in Psychology, HR Management, or Organizational Behavior",
    },
    {
      title: 'Market Research Analyst',
      description:
        'Market research analysts study consumer behavior and market conditions to help companies understand what products people want, who will buy them, and at what price. Psychology graduates excel at designing surveys and interpreting behavioral data.',
      medianSalary: '$74,680',
      growthRate: '13% (much faster than average)',
      education: "Bachelor's in Psychology, Marketing, or Statistics",
    },
    {
      title: 'Social Worker (with further education)',
      description:
        'Social workers help people cope with challenges in their lives, including mental health issues, poverty, abuse, and addiction. A psychology degree provides the foundation, with a Master of Social Work (MSW) typically required for clinical practice.',
      medianSalary: '$58,380',
      growthRate: '7% (faster than average)',
      education: "Bachelor's in Psychology + Master's in Social Work (MSW)",
    },
  ],
  4: [
    {
      title: 'Software Developer',
      description:
        'Software developers design, build, and maintain applications and systems that run on computers and devices. They write code, debug programs, and collaborate with teams to create everything from mobile apps to enterprise software platforms.',
      medianSalary: '$132,270',
      growthRate: '25% (much faster than average)',
      education: "Bachelor's in Computer Science or Software Engineering",
    },
    {
      title: 'Cybersecurity Analyst',
      description:
        "Cybersecurity analysts protect organizations' computer systems and networks from cyber threats. They monitor for security breaches, investigate incidents, implement security measures, and develop disaster recovery plans.",
      medianSalary: '$120,360',
      growthRate: '32% (much faster than average)',
      education: "Bachelor's in Cybersecurity, Computer Science, or IT",
    },
    {
      title: 'IT Manager',
      description:
        "IT managers plan and coordinate an organization's technology activities, including hardware, software, and network infrastructure. They oversee IT staff, manage budgets, and ensure systems support business objectives securely and efficiently.",
      medianSalary: '$164,070',
      growthRate: '15% (much faster than average)',
      education: "Bachelor's in IT, Computer Science, or Information Systems",
    },
    {
      title: 'Data Scientist',
      description:
        'Data scientists analyze large datasets to identify patterns, build predictive models, and generate insights that drive business decisions. They use programming, statistics, and machine learning to solve complex problems across industries.',
      medianSalary: '$108,020',
      growthRate: '35% (much faster than average)',
      education:
        "Bachelor's or Master's in Data Science, Computer Science, or Statistics",
    },
    {
      title: 'Network Administrator',
      description:
        "Network administrators install, configure, and maintain an organization's local area networks (LANs), wide area networks (WANs), and internet systems. They troubleshoot connectivity issues and ensure network security and performance.",
      medianSalary: '$95,360',
      growthRate: '3% (about average)',
      education:
        "Bachelor's in IT, Network Administration, or Computer Science",
    },
  ],
  5: [
    {
      title: 'Healthcare Administrator',
      description:
        'Healthcare administrators manage the business operations of hospitals, clinics, and other healthcare facilities. They coordinate medical services, manage budgets, ensure regulatory compliance, and work to improve the quality and efficiency of patient care.',
      medianSalary: '$110,680',
      growthRate: '28% (much faster than average)',
      education: "Bachelor's or Master's in Healthcare Administration",
    },
    {
      title: 'Medical Records Manager',
      description:
        'Medical records managers oversee the maintenance and security of patient health information. They ensure data accuracy, manage electronic health record (EHR) systems, and ensure compliance with HIPAA and other healthcare regulations.',
      medianSalary: '$62,990',
      growthRate: '16% (much faster than average)',
      education:
        "Bachelor's in Health Information Management or Healthcare Administration",
    },
    {
      title: 'Clinical Director',
      description:
        'Clinical directors oversee the clinical operations of healthcare facilities, ensuring quality patient care, managing staff, and developing policies and procedures. They bridge the gap between administrative leadership and clinical practice.',
      medianSalary: '$110,680',
      growthRate: '28% (much faster than average)',
      education:
        "Master's in Healthcare Administration or related clinical field",
    },
    {
      title: 'Public Health Educator',
      description:
        'Public health educators develop programs and materials to teach people about healthy behaviors. They assess community health needs, plan health education strategies, and evaluate the effectiveness of health programs and policies.',
      medianSalary: '$62,860',
      growthRate: '7% (faster than average)',
      education: "Bachelor's or Master's in Public Health or Health Education",
    },
  ],
  6: [
    {
      title: 'Elementary School Teacher',
      description:
        'Elementary school teachers educate children in grades K–5 across core subjects including reading, math, science, and social studies. They create lesson plans, assess student progress, and foster a positive learning environment for young learners.',
      medianSalary: '$61,690',
      growthRate: '1% (slower than average)',
      education: "Bachelor's in Education + State Teaching License",
    },
    {
      title: 'High School Teacher',
      description:
        'High school teachers specialize in specific subjects and prepare students for college, careers, and adult life. They develop curriculum, grade assignments, and mentor students through critical academic and personal development years.',
      medianSalary: '$62,360',
      growthRate: '1% (slower than average)',
      education:
        "Bachelor's in Education or Subject Area + State Teaching License",
    },
    {
      title: 'Instructional Coordinator',
      description:
        'Instructional coordinators develop curricula, select textbooks, train teachers, and assess educational program effectiveness. They work at the district or school level to ensure teaching standards are met and student outcomes improve.',
      medianSalary: '$66,490',
      growthRate: '2% (slower than average)',
      education:
        "Master's in Education, Curriculum & Instruction, or related field",
    },
    {
      title: 'Special Education Teacher',
      description:
        'Special education teachers work with students who have learning disabilities, emotional or behavioral disorders, and physical disabilities. They adapt lessons, develop Individualized Education Programs (IEPs), and collaborate with parents and specialists.',
      medianSalary: '$62,950',
      growthRate: '4% (as fast as average)',
      education: "Bachelor's in Special Education + State Teaching License",
    },
  ],
  7: [
    {
      title: 'Construction Manager',
      description:
        'Construction managers plan, coordinate, and oversee construction projects from start to finish. They manage budgets, schedules, and teams of workers, ensuring projects meet quality standards, safety codes, and client expectations.',
      medianSalary: '$104,900',
      growthRate: '5% (faster than average)',
      education: "Bachelor's in Construction Management or related field",
    },
    {
      title: 'HVAC Technician',
      description:
        'HVAC technicians install, maintain, and repair heating, ventilation, air conditioning, and refrigeration systems. They work in residential, commercial, and industrial settings, ensuring climate control systems operate efficiently and safely.',
      medianSalary: '$57,300',
      growthRate: '6% (faster than average)',
      education: "Postsecondary certificate or Associate's in HVAC Technology",
    },
    {
      title: 'Electrician',
      description:
        'Electricians install, maintain, and repair electrical wiring, equipment, and fixtures in buildings and structures. They read blueprints, follow electrical codes, and ensure systems are safe and functional in homes, businesses, and industrial facilities.',
      medianSalary: '$61,590',
      growthRate: '6% (faster than average)',
      education:
        'Apprenticeship or Postsecondary certificate in Electrical Technology',
    },
    {
      title: 'Automotive Service Technician',
      description:
        'Automotive service technicians diagnose, repair, and maintain vehicles. They use diagnostic equipment to identify problems, perform routine maintenance, and repair or replace faulty components in cars, trucks, and other motor vehicles.',
      medianSalary: '$46,970',
      growthRate: '2% (slower than average)',
      education:
        "Postsecondary certificate or Associate's in Automotive Technology",
    },
  ],
  8: [
    {
      title: 'Public Relations Specialist',
      description:
        'PR specialists create and maintain a favorable public image for their clients or employers. They write press releases, develop media strategies, manage social media presence, and coordinate public events and communications campaigns.',
      medianSalary: '$67,440',
      growthRate: '6% (faster than average)',
      education: "Bachelor's in Communications, PR, English, or Liberal Arts",
    },
    {
      title: 'Writer/Editor',
      description:
        'Writers and editors create content for books, magazines, websites, and other media. They research topics, write original content, revise drafts, and ensure accuracy and clarity. Liberal arts graduates excel in these roles due to strong writing and analytical skills.',
      medianSalary: '$73,080',
      growthRate: '4% (as fast as average)',
      education:
        "Bachelor's in English, Journalism, Liberal Arts, or Communications",
    },
    {
      title: 'Sales Manager',
      description:
        'Sales managers direct sales teams, set goals, analyze data, and develop training programs. They create strategies to meet revenue targets and build relationships with key clients. Strong communication and analytical skills from a liberal arts background are highly valued.',
      medianSalary: '$135,160',
      growthRate: '4% (as fast as average)',
      education: "Bachelor's in Business, Liberal Arts, or Marketing",
    },
    {
      title: 'Event Planner',
      description:
        'Event planners coordinate all aspects of professional meetings, conferences, and special events. They select venues, arrange catering, manage budgets, and ensure events run smoothly. Organizational and interpersonal skills are essential.',
      medianSalary: '$56,920',
      growthRate: '8% (faster than average)',
      education: "Bachelor's in Hospitality, Communications, or Liberal Arts",
    },
    {
      title: 'Non-profit Director',
      description:
        'Non-profit directors oversee the operations, fundraising, and strategic direction of non-profit organizations. They manage staff, develop programs, cultivate donor relationships, and ensure the organization fulfills its mission effectively.',
      medianSalary: '$77,030',
      growthRate: '6% (faster than average)',
      education:
        "Bachelor's in Liberal Arts, Public Administration, or Non-profit Management",
    },
  ],
  9: [
    {
      title: 'Administrative Services Manager',
      description:
        'Administrative services managers plan, direct, and coordinate supportive services of an organization. They oversee facilities, manage records, and ensure efficient day-to-day operations across departments.',
      medianSalary: '$104,900',
      growthRate: '5% (faster than average)',
      education: "Bachelor's in General Studies, Business, or related field",
    },
    {
      title: 'Customer Success Manager',
      description:
        "Customer success managers work to ensure clients achieve their desired outcomes while using a company's products or services. They build relationships, identify upsell opportunities, and reduce customer churn through proactive engagement.",
      medianSalary: '$65,000',
      growthRate: 'Growing demand in SaaS and tech industries',
      education: "Bachelor's in General Studies, Business, or Communications",
    },
    {
      title: 'Project Coordinator',
      description:
        'Project coordinators assist project managers in organizing and tracking project activities. They maintain schedules, prepare reports, facilitate communication between teams, and ensure deliverables are completed on time and within budget.',
      medianSalary: '$60,430',
      growthRate: '6% (faster than average)',
      education:
        "Bachelor's in General Studies, Business, or Project Management",
    },
    {
      title: 'Sales Representative',
      description:
        'Sales representatives sell products and services to businesses and consumers. They identify leads, make presentations, negotiate contracts, and build long-term client relationships. Strong communication skills are essential for success.',
      medianSalary: '$65,630',
      growthRate: '1% (slower than average)',
      education: "Bachelor's in General Studies, Business, or Marketing",
    },
  ],
  10: [
    {
      title: 'Registered Nurse (BSN)',
      description:
        'BSN-prepared registered nurses provide and coordinate patient care, educate patients about health conditions, and provide emotional support. A BSN opens doors to leadership roles, specialized units, and higher earning potential compared to ADN-prepared nurses.',
      medianSalary: '$86,070',
      growthRate: '6% (faster than average)',
      education: 'Bachelor of Science in Nursing (BSN)',
    },
    {
      title: 'Nurse Manager',
      description:
        'Nurse managers oversee nursing staff on specific units or departments, manage budgets, ensure quality patient care, and serve as a bridge between administration and bedside nurses. They combine clinical expertise with leadership skills.',
      medianSalary: '$101,340',
      growthRate: '28% (much faster than average)',
      education: 'BSN required; MSN preferred for advancement',
    },
    {
      title: 'Clinical Nurse Leader',
      description:
        'Clinical nurse leaders coordinate care delivery, implement evidence-based practices, and improve patient outcomes at the unit level. They assess risks, design care plans, and mentor other nurses in best practices.',
      medianSalary: '$95,000',
      growthRate: 'Growing role in healthcare systems',
      education: 'Master of Science in Nursing (MSN) with CNL certification',
    },
    {
      title: 'Public Health Nurse',
      description:
        'Public health nurses work in communities to promote health, prevent disease, and improve population health outcomes. They conduct screenings, develop health education programs, and respond to public health emergencies.',
      medianSalary: '$78,740',
      growthRate: '6% (faster than average)',
      education: 'BSN required; MPH or MSN beneficial',
    },
  ],
  11: [
    {
      title: 'Public Relations Specialist',
      description:
        'PR specialists craft and maintain a positive public image for organizations. They write press releases, manage media relationships, develop communication strategies, and handle crisis communications across traditional and digital channels.',
      medianSalary: '$67,440',
      growthRate: '6% (faster than average)',
      education: "Bachelor's in Communications, PR, or Journalism",
    },
    {
      title: 'Social Media Manager',
      description:
        'Social media managers develop and execute social media strategies, create content, manage online communities, and analyze engagement metrics. They help brands build awareness, drive traffic, and connect with audiences across platforms.',
      medianSalary: '$68,230',
      growthRate: '14% (much faster than average)',
      education: "Bachelor's in Communications, Marketing, or Digital Media",
    },
    {
      title: 'Technical Writer',
      description:
        'Technical writers create documentation that translates complex information into clear, concise content. They write user manuals, help guides, white papers, and online help systems for technology, healthcare, and engineering companies.',
      medianSalary: '$79,960',
      growthRate: '7% (faster than average)',
      education: "Bachelor's in Communications, English, or Technical Writing",
    },
    {
      title: 'Journalist',
      description:
        'Journalists research, write, and report news stories for print, broadcast, and digital media. They conduct interviews, verify facts, and present information in an engaging and accurate manner to inform the public.',
      medianSalary: '$57,500',
      growthRate: '-3% (declining in traditional media, growing in digital)',
      education: "Bachelor's in Journalism, Communications, or Media Studies",
    },
    {
      title: 'Marketing Coordinator',
      description:
        'Marketing coordinators support marketing campaigns by coordinating events, managing social media accounts, creating content, and tracking campaign performance. They work closely with marketing managers to execute brand strategies.',
      medianSalary: '$56,850',
      growthRate: '6% (faster than average)',
      education: "Bachelor's in Marketing, Communications, or Business",
    },
  ],
  12: [
    {
      title: 'Graphic Designer',
      description:
        'Graphic designers create visual concepts using software to communicate ideas that inspire, inform, and captivate consumers. They develop layouts for advertisements, brochures, magazines, websites, and corporate identity materials.',
      medianSalary: '$57,990',
      growthRate: '3% (about average)',
      education: "Bachelor's in Graphic Design or Visual Arts",
    },
    {
      title: 'UX/UI Designer',
      description:
        'UX/UI designers create intuitive, user-friendly interfaces for websites, apps, and digital products. They conduct user research, create wireframes and prototypes, and collaborate with developers to ensure seamless user experiences.',
      medianSalary: '$83,240',
      growthRate: '16% (much faster than average)',
      education: "Bachelor's in Design, HCI, or related field",
    },
    {
      title: 'Art Director',
      description:
        'Art directors are responsible for the overall visual style of publications, product packaging, movies, and advertising campaigns. They lead design teams, develop creative concepts, and ensure visual consistency across all materials.',
      medianSalary: '$105,180',
      growthRate: '4% (as fast as average)',
      education:
        "Bachelor's in Graphic Design, Fine Arts, or Visual Communications",
    },
    {
      title: 'Web Designer',
      description:
        'Web designers create the look, layout, and features of websites. They combine design principles with technical skills in HTML, CSS, and design tools to build visually appealing and functional websites that meet client needs.',
      medianSalary: '$80,730',
      growthRate: '8% (faster than average)',
      education:
        "Bachelor's in Web Design, Graphic Design, or Computer Science",
    },
    {
      title: 'Animator',
      description:
        'Animators create sequences of images that form animations for entertainment, advertising, video games, and educational content. They use specialized software to bring characters, environments, and stories to life through motion.',
      medianSalary: '$98,950',
      growthRate: '4% (as fast as average)',
      education: "Bachelor's in Animation, Fine Arts, or Computer Graphics",
    },
  ],
  13: [
    {
      title: 'Clergy/Minister',
      description:
        'Clergy members lead religious services, provide spiritual guidance, perform ceremonies, and counsel congregants. They serve as community leaders, offering support during life transitions and crises while managing the operations of their religious organizations.',
      medianSalary: '$55,190',
      growthRate: '4% (as fast as average)',
      education:
        "Bachelor's or Master's in Divinity, Theology, or Religious Studies",
    },
    {
      title: 'Religious Educator',
      description:
        'Religious educators develop and teach curricula about faith traditions, ethics, and spiritual development. They work in churches, schools, and community organizations, creating programs for children, youth, and adults.',
      medianSalary: '$52,370',
      growthRate: '4% (as fast as average)',
      education: "Bachelor's in Religious Education, Theology, or Education",
    },
    {
      title: 'Non-profit Director',
      description:
        'Non-profit directors lead faith-based and secular organizations, managing operations, fundraising, staff, and program development. Religious studies graduates bring strong ethical foundations and community-building skills to these leadership roles.',
      medianSalary: '$77,030',
      growthRate: '6% (faster than average)',
      education:
        "Bachelor's in Religious Studies, Non-profit Management, or Public Administration",
    },
    {
      title: 'Chaplain',
      description:
        'Chaplains provide spiritual care and emotional support in hospitals, military settings, prisons, and universities. They counsel individuals of all faiths during difficult times, lead worship services, and serve as a compassionate presence.',
      medianSalary: '$60,850',
      growthRate: '4% (as fast as average)',
      education:
        'Master of Divinity or equivalent + Clinical Pastoral Education (CPE)',
    },
    {
      title: 'Community Service Manager',
      description:
        'Community service managers coordinate and supervise social service programs and organizations. They assess community needs, manage staff and volunteers, oversee budgets, and ensure programs effectively serve their target populations.',
      medianSalary: '$74,240',
      growthRate: '9% (faster than average)',
      education:
        "Bachelor's in Religious Studies, Social Work, or Public Administration",
    },
  ],
}
